/**
 * Client-side environment configuration.
 *
 * SECURITY NOTE: Only VITE_* prefixed variables are exposed to the client.
 * These should ONLY contain:
 * - API URLs (public endpoints)
 * - Public keys (e.g., Stripe publishable key)
 * - Feature flags
 *
 * NEVER include:
 * - API secrets or private keys
 * - Database credentials
 * - OAuth client secrets
 * - Any sensitive tokens
 */

interface ClientEnv {
  /** Main API URL (backend-main, port 3000) */
  VITE_API_URL: string
  /** Auth service URL (backend-auth, port 3002) */
  VITE_AUTH_API_URL: string
  /** Shop API URL (backend-shop, port 3001) */
  VITE_SHOP_API_URL: string
  /** Admin API URL (backend-admin, port 3004) */
  VITE_ADMIN_API_URL: string
  /** Streamer API URL (backend-streamer, port 3006) */
  VITE_STREAMER_API_URL: string
  /** Main frontend URL (for links) */
  VITE_MAIN_URL: string
  /** Shop frontend URL (for links) */
  VITE_SHOP_URL: string
  /** WebSocket URL for backend-streamer */
  VITE_WEBSOCKET_URL: string
  /** Stripe publishable key (safe to expose) */
  VITE_STRIPE_PUBLIC_KEY?: string
  /** Odoo public web URL, usually the shop image proxy */
  VITE_ODOO_WEB_URL: string
  /** Odoo admin/operator web URL for admin deep links */
  VITE_ODOO_ADMIN_WEB_URL: string
}

// Port mapping reference:
// backend-main:     3000
// backend-shop:     3001
// backend-auth:     3002
// backend-admin:    3004
// backend-streamer: 3006

type ClientEnvKey = keyof ClientEnv

const LOCALHOST_PATTERN = /^(https?:\/\/)?(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/|$)/i
const DEVELOPMENT_URLS: Partial<Record<ClientEnvKey, string>> = import.meta.env.DEV
  ? {
      VITE_API_URL: 'http://localhost:3000',
      VITE_AUTH_API_URL: 'http://localhost:3002',
      VITE_SHOP_API_URL: 'http://localhost:3001',
      VITE_ADMIN_API_URL: 'http://localhost:3004',
      VITE_STREAMER_API_URL: 'http://localhost:3006',
      VITE_MAIN_URL: 'http://localhost:5173',
      VITE_SHOP_URL: 'http://localhost:5174',
      VITE_WEBSOCKET_URL: 'ws://localhost:3006',
      VITE_ODOO_WEB_URL: 'http://localhost:8069',
      VITE_ODOO_ADMIN_WEB_URL: 'http://localhost:8069',
    }
  : {}

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function isValidHttpOrWsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'ws:' || url.protocol === 'wss:'
  } catch {
    return false
  }
}

function readUrl(key: ClientEnvKey, value: string | undefined, options?: { allowWebSocket?: boolean }): string {
  const resolvedValue = value || DEVELOPMENT_URLS[key]
  const isValid =
    options?.allowWebSocket === true
      ? Boolean(resolvedValue && isValidHttpOrWsUrl(resolvedValue))
      : Boolean(resolvedValue && isValidHttpUrl(resolvedValue))

  if (!resolvedValue || !isValid) {
    throw new Error(`[client-env] Missing or invalid ${key}. Expected an absolute URL.`)
  }

  if (import.meta.env.PROD && LOCALHOST_PATTERN.test(resolvedValue)) {
    throw new Error(`[client-env] ${key} points to localhost in a production build: ${resolvedValue}`)
  }

  return resolvedValue
}

/**
 * Get typed client environment variables.
 * Validates URL variables and only allows localhost fallbacks in non-production builds.
 */
function createClientEnv(): ClientEnv {
  return {
    VITE_API_URL: readUrl('VITE_API_URL', import.meta.env.VITE_API_URL),
    VITE_AUTH_API_URL: readUrl('VITE_AUTH_API_URL', import.meta.env.VITE_AUTH_API_URL),
    VITE_SHOP_API_URL: readUrl('VITE_SHOP_API_URL', import.meta.env.VITE_SHOP_API_URL),
    VITE_ADMIN_API_URL: readUrl('VITE_ADMIN_API_URL', import.meta.env.VITE_ADMIN_API_URL),
    VITE_STREAMER_API_URL: readUrl('VITE_STREAMER_API_URL', import.meta.env.VITE_STREAMER_API_URL),
    VITE_MAIN_URL: readUrl('VITE_MAIN_URL', import.meta.env.VITE_MAIN_URL),
    VITE_SHOP_URL: readUrl('VITE_SHOP_URL', import.meta.env.VITE_SHOP_URL),
    VITE_WEBSOCKET_URL: readUrl('VITE_WEBSOCKET_URL', import.meta.env.VITE_WEBSOCKET_URL, { allowWebSocket: true }),
    VITE_STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
    VITE_ODOO_WEB_URL: readUrl('VITE_ODOO_WEB_URL', import.meta.env.VITE_ODOO_WEB_URL),
    VITE_ODOO_ADMIN_WEB_URL: readUrl(
      'VITE_ODOO_ADMIN_WEB_URL',
      import.meta.env.VITE_ODOO_ADMIN_WEB_URL || import.meta.env.VITE_ODOO_WEB_URL,
    ),
  }
}

/**
 * Singleton instance of client environment.
 * Use this for type-safe access to env variables.
 */
export const clientEnv = createClientEnv()
